<script>
import { required, requiredIf, email } from "vuelidate/lib/validators";

// Custom Components
import superService from "@/helpers/APIService/super.service";
import Middleware from "@/helpers/middlewares";
import errorHandler from "@/helpers/errorHandler";
import handleLogic from "@/helpers/handleLogic";

export default {
  data() {
    return {
      user_form: {
        first_name: "",
        last_name: "",
        email: "",
        mobile: "",
        mobile_iso: "US",
        password: "",
      },
      submitted: false,
      isBtnLoading: false,
      isError: false,
      errorMessage: null,
      mobileNoToBeSave: null,
      isPhoneNoValid: true,
    };
  },
  props: {
    userDetails: Object,
    isEdit: Boolean,
  },
  emits: ["submitted", "cancelSubmit"],
  validations: {
    user_form: {
      first_name: { required },
      last_name: { required },
      password: {
        required: requiredIf(function () {
          if (!this.isEdit) {
            return true;
          }
        }),
      },
      email: { required, email },
    },
  },
  mounted() {
    this.setValue();
  },
  methods: {
    // Method is used to set default value on edit user
    setValue() {
      if (this.isEdit) {
        Object.keys(this.userDetails).map((key) => {
          if (
            this.userDetails[key] !== null ||
            this.userDetails[key] !== undefined
          ) {
            this.user_form[key] = this.userDetails[key];
            if (key == "password") {
              this.user_form[key] = "********";
            }
          }
        });
      } else {
        this.generatePassword();
      }
    },
    // User Form Submit
    onSubmit() {
      this.submitted = true;
      this.isBtnLoading = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.isBtnLoading = false;
        return;
      } else {
        if (!this.isPhoneNoValid) {
          this.isBtnLoading = false;
          return;
        }
        this.user_form.mobile = this.mobileNoToBeSave;
        if (this.isEdit) {
          // Edit User
          if (this.user_form.password == "********") {
            delete this.user_form.password;
          }
          delete this.user_form.profile_picture;
          superService
            .editCdxUser(this.user_form.id, this.user_form)
            .then((response) => {
              Middleware.notification("Successfully updated !!", "success");
              this.isBtnLoading = false;
              this.$emit("submitted", response);
            })
            .catch((error) => {
              if (error.response.status != 500) {
                this.isError = true;
                this.isBtnLoading = false;
                this.errorMessage = error.response.data;
              }
              this.isBtnLoading = false;
              errorHandler(error);
            });
        } else {
          // Add User
          superService
            .addCdxUser(this.user_form)
            .then((response) => {
              Middleware.notification("Successfully added !!", "success");
              this.isBtnLoading = false;
              this.$emit("submitted", response);
            })
            .catch((error) => {
              if (error.response.status != 500) {
                this.isError = true;
                this.isBtnLoading = false;
                this.errorMessage = error.response.data;
              }
              this.isBtnLoading = false;
              errorHandler(error);
            });
        }
      }
    },
    // Method is use for cancel to add new building
    cancelSubmit() {
      this.$emit("cancelSubmit");
    },
    handleValidation(number) {
      if (number) {
        const data = handleLogic.phoneNumberHandle(number);
        if (data) {
          this.user_form.mobile_iso = data.number_iso;
          this.isPhoneNoValid = data.valid;
          this.mobileNoToBeSave = data.number;
        }
      }
    },
    // Method Generate Password
    generatePassword() {
      let length = 12,
        charset =
          "abcdefghijklmnopqrstuvwxyz0123456789ABCDEFGHIJKLMNOPQSTUVWXYZ",
        retVal = "";
      for (var i = 0, n = charset.length; i < length; ++i) {
        retVal += charset.charAt(Math.floor(Math.random() * n));
      }
      this.user_form.password = retVal;
    },
  },
};
</script>

<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-md-12">
        <h4>{{ isEdit ? "Update" : "Add New" }} User Details</h4>
        <hr />
      </div>
      <b-form @submit.prevent="onSubmit">
        <div class="row">
          <!-- Basic Details -->
          <h4 class="card-title">Basic Details</h4>
          <hr />

          <div class="col-md-4 col-sm-6">
            <b-form-group class="mb-3" label-for="first_name">
              <span slot="label">
                First Name<span class="text-danger">*</span></span
              >
              <b-form-input
                id="first_name"
                type="text"
                autocomplete="off"
                v-model="user_form.first_name"
                :class="{
                  'is-invalid': submitted && $v.user_form.first_name.$error,
                }"
              ></b-form-input>
              <div
                v-if="submitted && $v.user_form.first_name.$error"
                class="invalid-feedback"
              >
                <span v-if="!$v.user_form.first_name.required"
                  >First Name is required.</span
                >
              </div>
            </b-form-group>
          </div>
          <div class="col-md-4 col-sm-6">
            <b-form-group class="mb-3" label-for="last_name">
              <span slot="label">
                Last Name<span class="text-danger">*</span></span
              >
              <b-form-input
                id="last_name"
                type="text"
                autocomplete="off"
                v-model="user_form.last_name"
                :class="{
                  'is-invalid': submitted && $v.user_form.last_name.$error,
                }"
              ></b-form-input>
              <div
                v-if="submitted && $v.user_form.last_name.$error"
                class="invalid-feedback"
              >
                <span v-if="!$v.user_form.last_name.required"
                  >Last Name is required.</span
                >
              </div>
            </b-form-group>
          </div>
          <div class="col-md-4 col-sm-6">
            <b-form-group class="mb-3" label-for="password">
              <span slot="label">
                Password <span class="text-danger" v-if="!isEdit">*</span></span
              >
              <b-form-input
                id="password"
                type="text"
                autocomplete="off"
                v-model="user_form.password"
                :class="{
                  'is-invalid': submitted && $v.user_form.password.$error,
                }"
              >
                <template #prefix>
                  <LockOutlined class="site-form-item-icon" />
                </template>
              </b-form-input>
              <div
                v-if="submitted && $v.user_form.password.$error"
                class="invalid-feedback"
              >
                <span v-if="!$v.user_form.password.required"
                  >Password is required.</span
                >
              </div>
            </b-form-group>
          </div>

          <!-- Contact Details -->
          <h4 class="card-title">Contact Details</h4>
          <hr />

          <div class="col-md-4 col-sm-6">
            <b-form-group class="mb-3" label-for="email">
              <span slot="label">
                Email <span class="text-danger">*</span></span
              >
              <b-form-input
                id="email"
                type="text"
                autocomplete="off"
                placeholder="Type Email Address"
                v-model="user_form.email"
                :class="{
                  'is-invalid': submitted && $v.user_form.email.$error,
                }"
              ></b-form-input>
              <div
                v-if="submitted && $v.user_form.email.$error"
                class="invalid-feedback"
              >
                <span v-if="!$v.user_form.email.required"
                  >Email is required.</span
                >
                <span v-if="!$v.user_form.email.email">Enter valid email.</span>
              </div>
            </b-form-group>
          </div>
          <div class="col-md-4 col-sm-6">
            <b-form-group class="mb-3" label-for="mobile">
              <span slot="label">
                Mobile<span class="text-danger">*</span>
              </span>
              <vue-tel-input
                class="text-dark-gray"
                v-model="user_form.mobile"
                :validCharactersOnly="true"
                :autoDefaultCountry="true"
                :maxLen="11"
                :defaultCountry="
                  user_form
                    ? user_form.mobile_iso !== null
                      ? user_form.mobile_iso
                      : 'US'
                    : 'US'
                "
                @validate="handleValidation"
              ></vue-tel-input>
              <div
                class="invalid-feedback d-block"
                :class="
                  mobileNoToBeSave !== '' && !isPhoneNoValid
                    ? 'd-block'
                    : 'd-none'
                "
              >
                <span>Enter valid mobile number.</span>
              </div>
            </b-form-group>
          </div>

          <div class="col-md-12">
            <b-alert v-if="isError" show variant="danger">
              <ul>
                <li v-for="(error, index) in errorMessage" :key="index">
                  {{ index }}: {{ error[0] }}
                </li>
              </ul>
            </b-alert>
          </div>

          <div class="col-md-12">
            <b-button
              class="float-end ms-2"
              variant="outline-primary"
              type="reset"
              @click="cancelSubmit"
              >Cancel</b-button
            >
            <b-button
              class="float-end"
              variant="outline-primary"
              type="submit"
              :class="{
                disabled: isBtnLoading,
              }"
            >
              <i
                class="bx bx-loader bx-spin font-size-16 align-middle me-2"
                v-if="isBtnLoading"
              ></i>
              {{ isEdit ? "Update" : "Save" }}
            </b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>
